import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useAuth } from "@/auth/useAuth";
import { enforceRestrictedAssigneeFilter } from "@/features/task/utils/filterUtils";
import { getTasksWithFilters, getAllTasks, addTask, advanceTask, changeTaskState, deleteTask, updateTask, syncParentOnSubtaskDone, } from "@/features/task/services/taskService";
const getTasksKey = (page, filters, all, limit) => ["tasks", page, JSON.stringify(filters), all, limit];
const useTasks = (page = 1, filters = {}, all = false, limit = 10) => {
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const enforcedFilters = useMemo(() => enforceRestrictedAssigneeFilter(filters, user), [filters, user]);
    const queryKey = getTasksKey(page, enforcedFilters, all, limit);
    // Fetch tasks using a unified fetch function.
    const { data: tasksData, error, isLoading, refetch } = useQuery(queryKey, () => {
        // If "all" is true, call getAllTasks; otherwise, call getTasksWithFilters.
        return all
            ? getAllTasks(enforcedFilters)
            : getTasksWithFilters(page, enforcedFilters, limit);
    }, {
        keepPreviousData: true,
        staleTime: 60000, // 60 seconds.
    });
    // Mutation: Advance a task.
    const advanceTaskMutation = useMutation((taskId) => advanceTask(taskId), {
        onSuccess: () => {
            queryClient.invalidateQueries(queryKey);
            queryClient.invalidateQueries("taskStates");
            queryClient.invalidateQueries("taskStats");
        },
    });
    // Mutation: Update a task.
    const updateTaskMutation = useMutation(({ id, task }) => updateTask(id, task), {
        onSuccess: () => {
            queryClient.invalidateQueries(queryKey);
            queryClient.invalidateQueries("taskStates");
            queryClient.invalidateQueries("taskDetails");
            queryClient.invalidateQueries("taskStats");
        },
    });
    // Mutation: Change task state.
    const changeTaskStateMutation = useMutation(({ id, state }) => changeTaskState(id, state), {
        onSuccess: async (_data, variables) => {
            const { id, state } = variables;
            const current = queryClient.getQueryData(queryKey);
            const changedTask = current?.tasks.find(t => t.id === id);
            if (changedTask?.parent_id && state === 'done') {
                try {
                    await syncParentOnSubtaskDone(changedTask.parent_id, id);
                    queryClient.invalidateQueries(["taskDetails", changedTask.parent_id]);
                }
                catch (e) {
                    console.error(e);
                }
            }
            queryClient.invalidateQueries(queryKey);
            queryClient.invalidateQueries("taskStates");
            queryClient.invalidateQueries("tasks");
            queryClient.invalidateQueries(["taskDetails", id]);
            queryClient.invalidateQueries("taskStats");
        },
    });
    // Mutation: Add a task.
    const addTaskMutation = useMutation((task) => addTask(task), {
        onSuccess: () => {
            queryClient.invalidateQueries(queryKey);
            queryClient.invalidateQueries("taskStates");
            queryClient.invalidateQueries("taskStats");
        },
    });
    // Mutation: Delete a task.
    const deleteTaskMutation = useMutation((id) => deleteTask(id), {
        onSuccess: () => {
            queryClient.invalidateQueries(queryKey);
            queryClient.invalidateQueries("tasks");
            queryClient.invalidateQueries("timelineTasks");
            queryClient.invalidateQueries("taskStates");
            queryClient.invalidateQueries("taskStats");
        },
    });
    return {
        tasks: tasksData?.tasks || [],
        meta: tasksData?.meta || null,
        loading: isLoading,
        error: error ? error.message : null,
        refetchTasks: refetch,
        handleAdvanceTask: advanceTaskMutation.mutateAsync,
        handleUpdateTask: (id, task) => updateTaskMutation.mutateAsync({ id, task }),
        handleChangeTaskState: (id, state) => changeTaskStateMutation.mutateAsync({ id, state }).then(() => { }),
        handleAddTask: addTaskMutation.mutateAsync,
        handleDeleteTask: deleteTaskMutation.mutateAsync,
    };
};
export default useTasks;
